/**
 * Areas Index Content Component
 * 
 * Client-side component for displaying the areas index page
 * with all regions and their areas.
 */

'use client';

import Link from 'next/link';
import Image from 'next/image';
import { MapPin, ArrowRight, Compass } from '@phosphor-icons/react';
import type { AreaData, CoastalRegion } from '@routeroyal/theme-core';

interface RegionWithAreas {
  region: CoastalRegion;
  areas: Array<{
    slug: string;
    name: string;
    data?: AreaData;
  }>;
}

export interface AreasIndexMessages {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  cantDecideTitle?: string;
  cantDecideDescription?: string;
  cantDecideCta?: string;
}

interface AreasIndexContentProps {
  regions: RegionWithAreas[];
  locale: string;
  messages?: AreasIndexMessages;
}

export function AreasIndexContent({
  regions,
  locale,
  messages,
}: AreasIndexContentProps) {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-text)]">
          <div className="absolute inset-0 opacity-10">
            <Compass 
              size={800} 
              weight="thin" 
              className="absolute -bottom-40 -right-40 text-white/20 rotate-12" 
            />
          </div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 text-center text-white px-6">
          <p 
            className="text-[var(--color-primary)] text-xs font-bold tracking-[0.3em] uppercase mb-4 opacity-80 font-inter"
          >
            {messages?.eyebrow || 'Mauritius Real Estate'}
          </p>
          <h1 className="font-serif text-5xl md:text-7xl mb-6">
            {messages?.title || 'Explore by Area'}
          </h1>
          <p 
            className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto font-light font-inter"
          >
            {messages?.subtitle || "Discover exceptional properties across Mauritius' stunning coastlines. From vibrant resort towns to tranquil fishing villages."}
          </p>
        </div>
      </section>

      {/* Regions Grid */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {regions.map((regionGroup, regionIdx) => (
            <div key={regionGroup.region} className="mb-20 last:mb-0">
              {/* Region Header */}
              <div className="flex items-center gap-4 mb-10">
                <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-text)]">
                  {regionGroup.region}
                </h2>
                <div className="flex-1 h-px bg-gradient-to-r from-[var(--color-primary)]/30 to-transparent"></div>
              </div>

              {/* Areas Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {regionGroup.areas.map((area, areaIdx) => (
                  <Link
                    key={area.slug}
                    href={`/${locale}/areas/${area.slug}`}
                    className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:-translate-y-2"
                    style={{ 
                      animationDelay: `${regionIdx * 100 + areaIdx * 50}ms`,
                    }}
                  >
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      {area.data?.heroImage ? (
                        <Image
                          src={area.data.heroImage}
                          alt={area.name}
                          fill
                          className="object-cover group-hover:scale-110 transition-transform duration-700"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-text)]" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      
                      {/* Price Badge */}
                      {area.data?.priceRange && (
                        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-bold text-[var(--color-text)]">
                          {area.data.priceRange}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-serif text-2xl text-[var(--color-text)] group-hover:text-[var(--color-primary)] transition-colors">
                          {area.name}
                        </h3>
                        <ArrowRight 
                          size={20} 
                          className="text-gray-300 group-hover:text-[var(--color-primary)] group-hover:translate-x-1 transition-all mt-1" 
                          weight="bold"
                        />
                      </div>

                      {area.data?.description && (
                        <p 
                          className="text-[var(--color-text-muted)] text-sm line-clamp-2 mb-4 font-inter"
                        >
                          {area.data.description.split('.')[0]}.
                        </p>
                      )}

                      {/* Highlights */}
                      {area.data?.highlights && (
                        <div className="flex flex-wrap gap-2">
                          {area.data.highlights.slice(0, 2).map((highlight, idx) => (
                            <span 
                              key={idx}
                              className="text-[10px] font-medium text-[var(--color-primary)] bg-[var(--color-primary)]/5 px-2 py-1 rounded-full font-inter"
                            >
                              {highlight}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-gray-50 py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-text)] mb-6">
            {messages?.cantDecideTitle || "Can't Decide on a Location?"}
          </h2>
          <p 
            className="text-[var(--color-text-muted)] text-lg mb-8 max-w-2xl mx-auto font-inter"
          >
            {messages?.cantDecideDescription || 'Our expert agents know every corner of Mauritius. Let us help you find the perfect location based on your lifestyle and investment goals.'}
          </p>
          <Link
            href={`/${locale}/contact`}
            className="inline-flex items-center gap-2 bg-[var(--color-primary)] text-white px-8 py-4 rounded-full font-medium hover:bg-[var(--color-primary)]/90 transition-colors font-inter"
          >
            <MapPin size={18} weight="fill" />
            {messages?.cantDecideCta || 'Speak to an Agent'}
          </Link>
        </div>
      </section>
    </div>
  );
}
